import { Box, Center, Stack, Text } from "@chakra-ui/react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CapacityAllocationTypeEnum } from "@src/__generated__/graphql";
import { SortableItem } from "@src/components/modules/resource-planning/SortableItem";
import { AllocateCapacityColumnArea } from "@src/components/modules/resource-planning/timeline/AllocateCapacityColumnArea";
import type { PlanningDailyViewUserByDayModel } from "@src/components/modules/resource-planning/timeline/models";
import { useResourcePlanningDailyViewStore } from "@src/components/modules/resource-planning/timeline/ResourcePlanningDayPage";
import { UserCapacityStripForDay } from "@src/components/modules/resource-planning/timeline/UserCapacityStripForDay";
import { AllocationItemStyleEnum } from "@src/stores/models/WorkspaceSettings";
import { useStore } from "@src/utils/hooks";
import { HomeOfficeItem } from "@src/widgets/AllocationItem/HomeOfficeItem";
import { isWeekend } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { observer } from "mobx-react-lite";
import { type FunctionComponent, useMemo } from "react";

type AllocationsColumnDayProps = {
  day: PlanningDailyViewUserByDayModel["items"][0];
  user: PlanningDailyViewUserByDayModel["user"];
};

export const AllocationsColumnDay: FunctionComponent<AllocationsColumnDayProps> =
  observer(function AllocationsColumnDay({ day, user }) {
    const { workspaceStore } = useStore();
    const resourcePlanningStore = useResourcePlanningDailyViewStore();

    const nonWorkingDay = useMemo(() => {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return isWeekend(toZonedTime(day.date, timeZone)) || !!day.holiday;
    }, [day.date, day.holiday]);

    const greyStripes = useMemo(() => {
      return workspaceStore.settings?.default_planning_view_settings
        .allocation_item_style === AllocationItemStyleEnum.NO_COLOR
        ? "linear-gradient(135deg, #BFC7CE 2.38%, #eef0f2 2.38%, #eef0f2 50%, #BFC7CE 50%, #BFC7CE" +
            " 52.38%, #eef0f2 52.38%, #eef0f2 100%); background-size: 29.70px 29.70px;"
        : "linear-gradient(135deg, #BFC7CE 2.38%, #fff 2.38%, #fff 50%, #BFC7CE 50%, #BFC7CE" +
            " 52.38%, #fff 52.38%, #fff 100%); background-size: 29.70px 29.70px;";
    }, [
      workspaceStore.settings?.default_planning_view_settings
        .allocation_item_style,
    ]);
    const bgColor = useMemo(() => {
      return workspaceStore.settings?.default_planning_view_settings
        .allocation_item_style === AllocationItemStyleEnum.NO_COLOR
        ? "grey.50"
        : "white";
    }, [
      workspaceStore.settings?.default_planning_view_settings
        .allocation_item_style,
    ]);

    const items =
      day.items.filter(
        (item) => item.type !== CapacityAllocationTypeEnum.HomeOffice,
      ) ?? [];

    const ids = items.map(({ id }) => id);
    const homeOffice = useMemo(
      () =>
        day.items.find(
          (item) =>
            item.type === CapacityAllocationTypeEnum.HomeOffice ||
            item.type === CapacityAllocationTypeEnum.WeekendWork,
        ),
      [day.items],
    );

    const { setNodeRef } = useDroppable({
      id: day.containerId,
      data: {
        day: day,
        user: user,
        type: "column",
      },
      // TODO: add `satisfies DropColumn` when we will have support for `satisfies`
    });

    return (
      <Stack
        w="full"
        h="full"
        pt="2px"
        borderColor="grey.100"
        borderRightWidth="1px"
        borderBottomWidth="1px"
        spacing="0"
      >
        <UserCapacityStripForDay
          user={user}
          day={day}
          capacity={day.calculatedCapacity}
          workStatus={day.calculatedWorkStatus}
        />

        <Box
          ref={setNodeRef}
          // position `relative` is needed for the `absolute` positioned `CapacityAllocationItem`
          pos="relative"
          flex="1"
          overflow="hidden"
          p="2"
          bg={nonWorkingDay ? greyStripes : bgColor}
        >
          <Stack h="full">
            {!!day.holiday && (
              <Center
                flexWrap="wrap"
                p="2"
                textAlign="center"
                bg="grey.200"
                rounded="lg"
              >
                <Text fontSize="xs" fontWeight="semibold">
                  {day.holiday}
                </Text>
              </Center>
            )}

            {!!homeOffice && <HomeOfficeItem userItem={homeOffice} day={day} />}

            <SortableContext
              id={day.containerId}
              items={ids}
              strategy={verticalListSortingStrategy}
            >
              {items.map((userItem) => (
                <SortableItem key={userItem.id} userItem={userItem} day={day} />
              ))}
            </SortableContext>

            {
              // Hide column area while dragging for better performance
              !resourcePlanningStore.active ? (
                <AllocateCapacityColumnArea day={day} user={user} />
              ) : (
                // Add empty space to prevent layout jumping when dragging
                <Box minH="80px" />
              )
            }
          </Stack>
        </Box>
      </Stack>
    );
  });
